import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
// Import the service, NOT axios!
import * as authService from "./AuthServices";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for a user on initial load with error handling to prevent hangs
    try {
      const savedUser = localStorage.getItem("gatep_user");
      if (savedUser && savedUser !== "undefined" && savedUser !== "null") {
        setUser(JSON.parse(savedUser));
      }
    } catch (error) {
      console.error("Failed to parse saved user from localStorage:", error);
      localStorage.removeItem("gatep_user"); // Clear corrupted data
    } finally {
      setLoading(false);
    }
  }, []);

  // This effect runs whenever the `user` state changes.
  useEffect(() => {
    const fetchUserNotifications = async () => {
      // If there's a user, fetch their notifications
      if (user) {
        try {
          const userNotifications = await authService.getNotifications();
          setNotifications(userNotifications);
        } catch (error) {
          console.error("Failed to fetch notifications in context", error);
          setNotifications([]); // Ensure it's an empty array on failure
        }
      } else {
        // If there is no user (logged out), clear the notifications
        setNotifications([]);
      }
    };

    fetchUserNotifications();
  }, [user]); // The dependency array ensures this runs ONLY when the user logs in or out.

  // --- CONTEXT FUNCTIONS ---

  const login = async (username_or_email, password) => {
    try {
      const userData = await authService.login(username_or_email, password);
      setUser(userData); // Update the state
      return { success: true, user: userData };
    } catch (err) {
      const errorMsg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message;
      return { success: false, error: errorMsg };
    }
  };

  const register = async (apiPayload) => {
    try {
      await authService.register(apiPayload);
      return { success: true };
    } catch (err) {
      // Logic for mapping backend errors to frontend fields
      const errorData = err.response?.data || {};
      const backendErrors = {};
      for (const key in errorData) {
        let newKey = key;
        if (key === "confirm_password") newKey = "confirmPassword";
        if (key === "phone_number") newKey = "phone";
        backendErrors[newKey] = Array.isArray(errorData[key])
          ? errorData[key].join(", ")
          : errorData[key];
      }
      return { success: false, errors: backendErrors };
    }
  };

  const verifyOtp = async (otpPayload) => {
    try {
      await authService.verifyOtp(otpPayload);
      return { success: true };
    } catch (err) {
      const error = err.response?.data?.error || "Invalid OTP.";
      return { success: false, error };
    }
  };

  const resendOtp = async (resendPayload) => {
    try {
      await authService.resendOtp(resendPayload);
      return { success: true };
    } catch (err) {
      const error = err.response?.data?.error || "Failed to resend OTP.";
      return { success: false, error };
    }
  };

  const forgotPassword = async (email) => {
    try {
      await authService.forgotPassword(email);
      return { success: true };
    } catch (err) {
      const error = err.response?.data?.error || "Failed to send reset email.";
      return { success: false, error };
    }
  };

  const logout = () => {
    authService.logout(); // Call the service to clear data
    setUser(null); // Update the state
    window.location.href = "/db-shiksha/kaas/login";
  };

  // +++ ADDED FUNCTION TO UPDATE NOTIFICATION STATE +++
  const updateNotification = async (notificationId, newStatus, isRead) => {
    try {
      // Call the service to update the backend
      await authService.updateNotificationStatus(
        notificationId,
        newStatus,
        isRead
      );

      // Update the local state for immediate UI feedback
      setNotifications((prevNotifications) =>
        prevNotifications.map((n) =>
          n.id === notificationId
            ? { ...n, status: newStatus, is_read: isRead }
            : n
        )
      );
    } catch (error) {
      console.error("Context failed to update notification:", error);
      // Optionally, show an error toast to the user
    }
  };

  const value = {
    user,
    loading,
    notifications,
    login,
    register,
    verifyOtp,
    resendOtp,
    forgotPassword,
    logout,
    updateNotification, // +++ EXPOSE THE NEW FUNCTION TO THE CONTEXT
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
